import type {
  ColumnDef,
  OnChangeFn,
  RowData,
  Table,
  VisibilityState,
} from "@tanstack/react-table";
import exportFromJSON, { type ExportType } from "export-from-json";
import { Download, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { DataTable, DataTableFacetedFilter } from "~/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";

function cellValueToExportString(value: unknown): string {
  if (value == null) {
    return "N/A";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "N/A";
}

const EXPORT_FORMATS = ["json", "xls", "csv"] as const;

function isExportType(value: string): value is ExportType {
  return (EXPORT_FORMATS as readonly string[]).includes(value);
}

export function SalesDataTable<TData extends RowData, TValue = unknown>({
  columns,
  id,
  data,
  hasToolbarSpacer,
  expandedContent,
}: {
  columns: ColumnDef<TData, TValue>[];
  id: string;
  data: TData[];
  hasToolbarSpacer?: boolean;
  expandedContent?: (row: TData) => React.ReactNode;
}) {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  return (
    <DataTable
      className="w-full overflow-x-auto"
      columns={columns}
      columnVisibility={columnVisibility}
      data={data}
      expandedContent={expandedContent}
      id={id}
      setColumnVisibility={setColumnVisibility}
      toolbar={(table) => (
        <TableToolbar
          columnVisibility={columnVisibility}
          hasSpacer={hasToolbarSpacer}
          setColumnVisibility={setColumnVisibility}
          table={table}
          tableId={id}
        />
      )}
      zebraStyle
    />
  );
}

const TableToolbar = <TData extends RowData>({
  table,
  columnVisibility,
  setColumnVisibility,
  tableId,
  hasSpacer,
}: {
  table: Table<TData>;
  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  tableId: string;
  hasSpacer?: boolean;
}) => {
  return (
    <div className="flex flex-wrap gap-2.5">
      {hasSpacer && <div className="w-44" />}
      {table
        .getAllColumns()
        .filter((c) => c.columnDef.meta?.faceting)
        .map((column) => (
          <DataTableFacetedFilter
            column={column}
            key={column.id}
            options={
              column.id === "timeFrame"
                ? [
                    { value: "DAILY", label: "Daily" },
                    { value: "WEEKLY", label: "Weekly" },
                    { value: "MONTHLY", label: "Monthly" },
                  ]
                : Object.values(
                    Object.fromEntries(
                      table
                        .getCoreRowModel()
                        .rows.map((r) =>
                          column.columnDef.meta?.faceting?.getOption(
                            r.getValue(column.id)
                          )
                        )
                        .filter(
                          (r): r is NonNullable<typeof r> => r !== undefined
                        )
                        .map((r) => [r.value, r])
                    )
                  )
            }
            singleSelect={column.id === "timeFrame"}
            title={column.columnDef.meta?.faceting?.title}
          />
        ))}
      <div className="flex-1" />
      <ExportMenu
        fileName={tableId}
        getData={() =>
          table
            .getRowModel()
            .rows.map((r) =>
              Object.fromEntries(
                r
                  .getAllCells()
                  .map((c) => [
                    typeof c.column.columnDef.header === "string"
                      ? c.column.columnDef.header
                      : c.id,
                    c.column.columnDef.meta?.getCellForExport?.(c.getValue()) ??
                      cellValueToExportString(c.getValue()),
                  ])
              )
            )
        }
      />
      <ViewMenu
        columns={table
          .getAllColumns()
          .map((c) =>
            typeof c.columnDef.header === "string"
              ? { id: c.id, name: c.columnDef.header }
              : null
          )
          .filter((item): item is NonNullable<typeof item> => item !== null)}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
      />
    </div>
  );
};

const ViewMenu = ({
  columns,
  columnVisibility,
  setColumnVisibility,
}: {
  columns: { id: string; name: string }[];
  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="text-muted-foreground aria-expanded:bg-muted gap-2 rounded-md"
          size="sm"
          type="button"
          variant="outline"
        >
          <SlidersHorizontal className="text-foreground size-5" />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {columns.map((column) => (
          <DropdownMenuCheckboxItem
            checked={
              columnVisibility[column.id] === undefined ||
              columnVisibility[column.id]
            }
            key={column.id}
            onCheckedChange={(v) =>
              setColumnVisibility((old) => ({ ...old, [column.id]: v }))
            }
          >
            {column.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ExportMenu = ({
  getData,
  fileName,
}: {
  getData: () => Record<string, string | boolean | number | null>[];
  fileName: string;
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="text-muted-foreground aria-expanded:bg-muted gap-2 rounded-md"
          size="sm"
          type="button"
          variant="outline"
        >
          <Download className="text-foreground size-5" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {EXPORT_FORMATS.map((format) => (
          <DropdownMenuItem
            className="uppercase"
            key={format}
            onClick={() => {
              if (!isExportType(format)) {
                return;
              }
              exportFromJSON({
                exportType: format,
                data: getData(),
                fileName,
              });
            }}
          >
            {format}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
